/**
 * Utility functions for audio handling with Safari compatibility
 */

/**
 * Detects the MIME type of an audio file based on its URL, filename, or Blob MIME.
 *
 * Optimistic voice notes use blob: URLs with no extension. Guessing audio/mpeg for
 * those breaks Safari (WebKitBlobResource 3) when the recording is actually audio/mp4.
 *
 * @param {string} url - The URL of the audio file
 * @param {string|{ fileName?: string, mimeType?: string }} [options] - Filename and/or Blob MIME hints
 * @returns {string} - The MIME type of the audio, or '' when unknown (omit <source type>)
 */
export const getAudioMimeType = (url, options = {}) => {
  const hints = typeof options === 'string'
    ? { fileName: options }
    : (options || {});
  const explicitMime = String(hints.mimeType || '').trim();

  if (explicitMime) {
    return explicitMime;
  }

  const haystack = [hints.fileName, url].filter(Boolean).join(' ').toLowerCase();

  if (!haystack) return 'audio/mpeg';

  // Check URL / filename for format hints
  if (haystack.includes('.ogg') || haystack.includes('audio/ogg') || haystack.includes('codecs=opus')) {
    return 'audio/ogg; codecs=opus';
  }
  if (haystack.includes('.opus')) {
    return 'audio/opus';
  }
  if (haystack.includes('.mp3') || haystack.includes('audio/mpeg')) {
    return 'audio/mpeg';
  }
  if (haystack.includes('.mp4') || haystack.includes('.m4a') || haystack.includes('audio/mp4')) {
    return 'audio/mp4';
  }
  if (haystack.includes('.wav')) {
    return 'audio/wav';
  }
  if (haystack.includes('.webm')) {
    return 'audio/webm';
  }
  if (haystack.includes('.aac')) {
    return 'audio/aac';
  }

  // blob: URLs carry no extension — let the browser sniff the Blob.type instead of
  // forcing audio/mpeg (Safari fails hard when the declared type is wrong).
  if (String(url || '').startsWith('blob:')) {
    return '';
  }

  // Default to mpeg for remote URLs with unknown extensions
  return 'audio/mpeg';
};

/**
 * Checks if the current browser is Safari
 * @returns {boolean}
 */
export const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
};

/**
 * Maps a recording MIME type to the file extension its upload should carry.
 *
 * MediaRecorder hands back a Blob and FormData names an unnamed Blob literally
 * "blob", so without this the object lands in S3 with no extension — and
 * getAudioMimeType, which decides the <source type> purely from the URL, then
 * falls back to audio/mpeg and the note will not play back.
 *
 * The reported type carries codec parameters ("audio/webm;codecs=opus"), so the
 * parameters are stripped before the lookup.
 *
 * @param {string} mimeType - The MediaRecorder MIME type
 * @returns {string} - Extension without the dot
 */
export const audioExtensionForMimeType = (mimeType) => {
  const base = String(mimeType || '').split(';')[0].trim().toLowerCase();

  const extensions = {
    'audio/mp4': 'm4a',
    'audio/m4a': 'm4a',
    'audio/x-m4a': 'm4a',
    'audio/aac': 'aac',
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/ogg': 'ogg',
    'audio/opus': 'opus',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/webm': 'webm'
  };

  if (extensions[base]) return extensions[base];
  // Safari voice notes are mp4/aac; defaulting to webm breaks upload/playback there.
  return isSafari() ? 'm4a' : 'webm';
};

/**
 * Wraps a MediaRecorder Blob as a named File with a reliable MIME for upload.
 * Unnamed Blobs land in FormData as "blob" with no extension and often fail on Safari.
 *
 * @param {Blob|File} blob
 * @returns {File}
 */
export const buildVoiceNoteFile = (blob) => {
  if (blob instanceof File && blob.name && blob.type) {
    return blob;
  }

  let mimeType = String(blob?.type || '').trim();
  if (!mimeType) {
    mimeType = isSafari() ? 'audio/mp4' : 'audio/webm';
  }

  return new File(
    [blob],
    `voice-note-${Date.now()}.${audioExtensionForMimeType(mimeType)}`,
    { type: mimeType }
  );
};

/**
 * Checks if the browser can play a specific audio type
 * @param {string} mimeType - The MIME type to check
 * @returns {boolean}
 */
export const canPlayAudioType = (mimeType) => {
  const audio = document.createElement('audio');
  const canPlay = audio.canPlayType(mimeType);
  return canPlay === 'probably' || canPlay === 'maybe';
};

/**
 * Ordered MediaRecorder candidates: first supported wins.
 *
 * A recording from the mic button has to reach the customer as a voice note,
 * and WhatsApp picks that rendering only for Ogg/Opus. Opus goes first so the
 * browser captures the delivered format directly: Firefox records Ogg/Opus and
 * Chrome WebM/Opus, both of which the server rewraps into Ogg with a lossless
 * stream copy.
 *
 * MP4 stays last on purpose. Safari cannot record Opus at all, so it lands
 * there and pays a re-encode — but it is also the browser that gets its
 * playback copy for free, since that one is AAC. Ordering MP4 first would
 * invert the cost onto everyone else.
 *
 * No UA sniffing: `isTypeSupported` answers per browser.
 */
const RECORDING_MIME_TYPE_CANDIDATES = [
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

/**
 * Gets the best supported MIME type for MediaRecorder.
 * @returns {string} - The best supported MIME type, or '' to let the browser choose
 */
export const getSupportedRecordingMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }

  for (const type of RECORDING_MIME_TYPE_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  // Return empty string to let browser choose default
  return '';
};

/**
 * Whether this browser can record audio at all. When false, the UI hides the
 * microphone and the agent falls back to uploading an audio file, which the
 * backend normalizes to AAC/m4a anyway.
 * @returns {boolean}
 */
export const canRecordAudio = () =>
  typeof MediaRecorder !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function';

/**
 * Determines if audio format is likely unsupported in current browser
 * Used to show appropriate error messages for Safari users
 * @param {string} url - The audio URL
 * @returns {boolean}
 */
export const isAudioFormatProblematic = (url) => {
  if (!isSafari()) return false;
  
  const mimeType = getAudioMimeType(url);
  if (!mimeType) return false;
  
  // OGG/Opus formats are not supported in Safari
  if (mimeType.includes('ogg') || mimeType.includes('opus')) {
    return !canPlayAudioType(mimeType);
  }
  
  return false;
};

export default {
  getAudioMimeType,
  audioExtensionForMimeType,
  buildVoiceNoteFile,
  isSafari,
  canPlayAudioType,
  getSupportedRecordingMimeType,
  canRecordAudio,
  isAudioFormatProblematic,
};

